<script setup lang="ts">
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { IconImage, Tooltip, ColoredButton, Menu } from '@/shared/ui';
  import { LogoutButton } from '@/features/Auth';
  import PencilIcon from '@/shared/assets/icons/PencilIcon.svg';
  import { useBranchLogo, SelectBranchInput } from '@/entities/Branch';
  import type { MenuItem, PermissionMatchMode } from '@/shared/lib';
  import { checkPermissions } from '@/shared/lib';
  import type { CaslAction, CaslSubject } from '@/shared/lib/casl';

  interface Props {
    isCollapsed: boolean;
    userName: string;
    userEmail: string;
    menuItems: MenuItem[];
    canCheck?: (action: CaslAction, subject: CaslSubject) => boolean;
    adminPermissions?: { action: CaslAction; subject: CaslSubject }[];
    adminMatchMode?: PermissionMatchMode;
  }

  const props = withDefaults(defineProps<Props>(), {
    adminPermissions: () => [{ action: 'read' as CaslAction, subject: 'Admin' as CaslSubject }],
    adminMatchMode: 'all',
  });

  const router = useRouter();
  const { currentLogoUrl, handleLogoError } = useBranchLogo();

  const hasAdminPermission = computed(
    () =>
      !props.canCheck ||
      checkPermissions(
        props.adminPermissions,
        (perm) => props.canCheck!(perm.action, perm.subject),
        props.adminMatchMode
      )
  );

  // Prioridade da primeira tela disponível ao acessar o painel admin
  const ADMIN_PRIORITY_ROUTES: { action: CaslAction; subject: CaslSubject; route: string }[] = [
    { action: 'managePermissions', subject: 'Admin', route: '/admin/permissoes/permissoes' },
    { action: 'read', subject: 'Admin', route: '/admin/usuarios' },
    { action: 'manageBranches', subject: 'Admin', route: '/admin/filiais' },
  ];

  const adminTargetRoute = computed(() => {
    if (!props.canCheck) {
      return '/admin';
    }
    const target = ADMIN_PRIORITY_ROUTES.find((item) => props.canCheck!(item.action, item.subject));
    return target?.route ?? '/admin';
  });

  const handleAdminRouter = () => {
    router.push(adminTargetRoute.value);
  };

  const emit = defineEmits<{
    expand: [];
  }>();
</script>

<template>
  <div class="flex flex-col h-screen w-full min-h-180">
    <div :class="[props.isCollapsed ? 'my-5' : 'py-8 pl-5 pr-3.5']" class="flex-1">
      <div :class="[props.isCollapsed ? 'flex flex-col items-center' : '']">
        <router-link
          to="/"
          data-testid="sidebar-logo-link"
          aria-label="Ir para a página inicial"
          class="inline-block"
        >
          <img
            :class="[props.isCollapsed ? 'w-14 mb-4' : 'w-26 my-2.5']"
            :src="currentLogoUrl"
            alt="Logo HTM"
            @error="handleLogoError"
          />
        </router-link>
        <p
          v-if="!props.isCollapsed"
          class="text-[var(--theme-text)] text-xs font-semibold text-left mt-3"
        >
          Gestor de Documentos 2.0
        </p>
        <p v-else class="flex flex-col items-center">...</p>
      </div>
      <Menu :is-collapsed="props.isCollapsed" :menu-items="menuItems" :can-check="canCheck" />
    </div>

    <div
      :class="[
        'mb-8 w-full flex flex-col gap-2 pb-7 px-5',
        { 'items-center': props.isCollapsed, 'items-start': !props.isCollapsed },
      ]"
    >
      <template v-if="hasAdminPermission">
        <ColoredButton
          v-if="!props.isCollapsed"
          data-testid="sidebar-admin-btn"
          text="Admin"
          :icon="PencilIcon"
          icon-alt="Administração"
          class="mr-1.5 text-sm!"
          @click="handleAdminRouter"
        />

        <Tooltip v-else content="Admin" placement="right">
          <button
            data-testid="sidebar-admin-btn"
            class="bg-[var(--theme-secondary)] text-white flex items-center gap-3 w-10 h-10 rounded-lg justify-center"
            @click="handleAdminRouter"
          >
            <IconImage :src="PencilIcon" alt="Gestor de Documentos" class="text-white" />
          </button>
        </Tooltip>
      </template>

      <SelectBranchInput
        :is-collapsed="props.isCollapsed"
        label="Filial"
        button-class="shadow rounded-lg"
        @expand="emit('expand')"
      />

      <LogoutButton
        class="mt-2"
        :is-collapsed="props.isCollapsed"
        :user-name="props.userName"
        :user-email="props.userEmail"
      />
    </div>
  </div>
</template>

<style scoped></style>
